import { Task, Lead, Company, Opportunity } from '../types';
import type { AIResponse, InteractiveCard } from './aiAssistantService';

interface CRMData {
  tasks?: Task[];
  contacts?: Lead[];
  opportunities?: Opportunity[];
  companies?: Company[];
}

const isOpenTask = (task: Task) => !task.completed && task.status !== 'completed';
const itemDate = (value: unknown) => value ? new Date(value as any).getTime() : Number.MAX_SAFE_INTEGER;

function formatDate(value: unknown): string {
  if (!value) return 'No date';
  const date = new Date(value as any);
  if (Number.isNaN(date.getTime())) return 'No date';
  return date.toLocaleDateString();
}

function taskCard(task: Task): InteractiveCard {
  const dueDate = new Date(task.dueDate as any);
  const isOverdue = isOpenTask(task) && !Number.isNaN(dueDate.getTime()) && dueDate < new Date();

  return {
    id: task.id,
    type: 'task',
    title: task.title,
    subtitle: task.description,
    priority: task.priority as 'high' | 'medium' | 'low',
    status: task.status || (task.completed ? 'completed' : 'pending'),
    details: {
      dueDate: formatDate(task.dueDate),
      priority: task.priority,
      status: task.status || (task.completed ? 'completed' : 'pending'),
      type: task.type,
      isOverdue
    },
    actions: [{ label: 'View Details', action: 'view', primary: true }],
    deepLink: {
      itemType: 'task',
      itemId: task.id,
      action: 'view',
      label: task.title
    }
  };
}

function contactCard(contact: Lead): InteractiveCard {
  const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.company || 'Contact';
  return {
    id: contact.id,
    type: 'contact',
    title: name,
    subtitle: contact.company,
    priority: (contact.score || 0) >= 80 ? 'high' : (contact.score || 0) >= 60 ? 'medium' : 'low',
    status: contact.stage,
    details: {
      email: contact.email,
      phone: contact.phone,
      company: contact.company,
      score: contact.score,
      stage: contact.stage
    },
    actions: [{ label: 'View Contact', action: 'view', primary: true }],
    deepLink: {
      itemType: 'contact',
      itemId: contact.id,
      action: 'view',
      label: name
    }
  };
}

function opportunityCard(opportunity: Opportunity): InteractiveCard {
  const title = opportunity.title || opportunity.name || opportunity.companyName || 'Opportunity';
  return {
    id: opportunity.id,
    type: 'opportunity',
    title,
    subtitle: opportunity.company?.name || opportunity.companyName,
    status: opportunity.stage,
    details: {
      value: opportunity.value,
      stage: opportunity.stage,
      companyName: opportunity.company?.name || opportunity.companyName,
      probability: opportunity.probability,
      expectedCloseDate: opportunity.expectedCloseDate
    },
    actions: [{ label: 'View Opportunity', action: 'view', primary: true }],
    deepLink: {
      itemType: 'opportunity',
      itemId: opportunity.id,
      action: 'view',
      label: title
    }
  };
}

function companyCard(company: Company): InteractiveCard {
  return {
    id: company.id,
    type: 'company',
    title: company.name,
    subtitle: company.industry,
    details: {
      domain: company.domain,
      industry: company.industry,
      employeeCount: company.employeeCount,
      revenue: company.revenue,
      isCustomer: company.isCustomer
    },
    actions: [{ label: 'View Company', action: 'view', primary: true }],
    deepLink: {
      itemType: 'company',
      itemId: company.id,
      action: 'view',
      label: company.name
    }
  };
}

function containsAny(query: string, terms: string[]): boolean {
  return terms.some(term => query.includes(term));
}

function isGreeting(query: string): boolean {
  const normalized = query.trim().toLowerCase().replace(/[!?.\s]+$/g, '');
  return [
    'hi',
    'hello',
    'hey',
    'hola',
    'hallo',
    'servus',
    'moin',
    'guten morgen',
    'guten tag',
    'good morning',
    'good afternoon',
    'good evening'
  ].includes(normalized);
}

function isThanks(query: string): boolean {
  return /^(thanks|thank you|danke|dankeschön|merci|gracias)\b/i.test(query.trim());
}

function appHelpResponse(query: string): AIResponse | null {
  const q = query.toLowerCase();
  const wantsLocation = containsAny(q, [
    'where', 'how do i', 'how can i', 'where can i', 'wo', 'wie kann ich', 'wo kann ich', 'where to'
  ]);

  if (!wantsLocation) return null;

  if (containsAny(q, ['lead', 'contact', 'kontakt', 'kontakte'])) {
    return {
      response: 'Use the Kontakte tab for contacts and leads. There you can add new contacts, edit details, track stages, notes, follow-ups, and open the exact contact record.',
      action: 'navigate_to_contacts',
      data: [],
      cards: []
    };
  }

  if (containsAny(q, ['task', 'tasks', 'aufgabe', 'aufgaben', 'todo', 'to-do'])) {
    return {
      response: 'Use the Aufgaben tab for tasks. You can create tasks, filter by owner or due date, open task details, and manage follow-ups from there.',
      action: 'navigate_to_tasks',
      data: [],
      cards: []
    };
  }

  if (containsAny(q, ['opportunity', 'opportunities', 'deal', 'deals', 'pipeline'])) {
    return {
      response: 'Use the Opportunities tab for deals and pipeline work. Open a deal there to update its stage, value, close date, linked contacts, and next actions.',
      action: 'navigate_to_opportunities',
      data: [],
      cards: []
    };
  }

  if (containsAny(q, ['company', 'companies', 'firma', 'firmen', 'organization', 'organisation'])) {
    return {
      response: 'Use the Companies tab for organization-level work. That is where company details, linked CRM contacts, company members, roles, and org structure belong.',
      action: 'navigate_to_companies',
      data: [],
      cards: []
    };
  }

  if (containsAny(q, ['time', 'zeiterfassung', 'hours', 'stunden'])) {
    return {
      response: 'Use Zeiterfassung for time entries. You can add single entries or bulk date ranges when the same working time applies across multiple days.',
      action: 'navigate_to_time_tracking',
      data: [],
      cards: []
    };
  }

  return null;
}

export const assistantFallbackService = {
  answer(query: string, crmData: CRMData = {}): AIResponse {
    const q = query.toLowerCase();
    const tasks = crmData.tasks || [];
    const contacts = crmData.contacts || [];
    const opportunities = crmData.opportunities || [];
    const companies = crmData.companies || [];
    const today = new Date();
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    if (isGreeting(query)) {
      return {
        response: 'Hi, I am here. You can ask me normal questions, ask where something is in the app, or ask about your tasks, contacts, companies, and opportunities.',
        action: 'small_talk',
        data: [],
        cards: []
      };
    }

    if (isThanks(query)) {
      return {
        response: 'You are welcome. Ask me what you want to do next and I will point you to the right place.',
        action: 'small_talk',
        data: [],
        cards: []
      };
    }

    const appHelp = appHelpResponse(query);
    if (appHelp) return appHelp;

    if (
      containsAny(q, ['create', 'add', 'new', 'remind', 'erstelle', 'erstellen', 'anlegen', 'hinzufügen', 'erinnere']) &&
      containsAny(q, ['task', 'tasks', 'aufgabe', 'aufgaben', 'todo', 'to-do'])
    ) {
      return {
        response: 'I can help you create tasks from the Aufgaben tab. Open Aufgaben and use the add button, or ask with the AI model enabled so I can turn your sentence into a task draft.',
        action: 'navigate_to_tasks',
        data: [],
        cards: []
      };
    }

    if (containsAny(q, ['overdue', 'überfällig'])) {
      const matches = tasks
        .filter(task => isOpenTask(task) && new Date(task.dueDate as any) < today)
        .sort((a, b) => itemDate(a.dueDate) - itemDate(b.dueDate));

      return {
        response: matches.length
          ? `I found ${matches.length} overdue open task${matches.length === 1 ? '' : 's'}. The oldest one is "${matches[0].title}".`
          : 'I did not find any overdue open tasks.',
        action: 'search_tasks',
        data: matches,
        cards: matches.slice(0, 12).map(taskCard)
      };
    }

    if (containsAny(q, ['today', 'heute'])) {
      const matches = tasks
        .filter(task => {
          const due = new Date(task.dueDate as any);
          return isOpenTask(task) && due >= today && due <= todayEnd;
        })
        .sort((a, b) => itemDate(a.dueDate) - itemDate(b.dueDate));

      return {
        response: matches.length
          ? `You have ${matches.length} open task${matches.length === 1 ? '' : 's'} due today.`
          : 'You have no open tasks due today.',
        action: 'search_tasks',
        data: matches,
        cards: matches.slice(0, 12).map(taskCard)
      };
    }

    if (containsAny(q, ['task', 'tasks', 'aufgabe', 'aufgaben'])) {
      let matches = tasks.filter(isOpenTask);
      if (containsAny(q, ['high', 'hoch', 'priority', 'priorität'])) {
        matches = matches.filter(task => task.priority === 'high');
      }
      matches = matches.sort((a, b) => itemDate(a.dueDate) - itemDate(b.dueDate));

      return {
        response: matches.length
          ? `I found ${matches.length} matching open task${matches.length === 1 ? '' : 's'}.`
          : 'I did not find matching open tasks.',
        action: 'search_tasks',
        data: matches,
        cards: matches.slice(0, 12).map(taskCard)
      };
    }

    if (containsAny(q, ['opportunity', 'opportunities', 'deal', 'deals', 'pipeline'])) {
      let matches = opportunities.filter(opp => opp.stage !== 'won' && opp.stage !== 'lost');
      if (containsAny(q, ['closing', 'negotiation', 'abschluss', 'verhandlung'])) {
        matches = matches.filter(opp => opp.stage === 'closing' || opp.stage === 'negotiation');
      }
      matches = matches.sort((a, b) => (b.value || 0) - (a.value || 0));
      const total = matches.reduce((sum, opp) => sum + (opp.value || 0), 0);

      return {
        response: matches.length
          ? `I found ${matches.length} active opportunit${matches.length === 1 ? 'y' : 'ies'} with a total value of ${total.toLocaleString()} EUR.`
          : 'I did not find active opportunities matching that request.',
        action: 'search_opportunities',
        data: matches,
        cards: matches.slice(0, 12).map(opportunityCard)
      };
    }

    if (containsAny(q, ['lead', 'contact', 'contacts', 'kontakt', 'kontakte'])) {
      let matches = contacts;
      if (containsAny(q, ['high', 'hot', 'hoch', 'priority', 'priorität'])) {
        matches = matches.filter(contact => (contact.score || 0) >= 60);
      }
      matches = matches.sort((a, b) => (b.score || 0) - (a.score || 0));

      return {
        response: matches.length
          ? `I found ${matches.length} matching contact${matches.length === 1 ? '' : 's'}.`
          : 'I did not find contacts matching that request.',
        action: 'search_contacts',
        data: matches,
        cards: matches.slice(0, 12).map(contactCard)
      };
    }

    if (containsAny(q, ['company', 'companies', 'firma', 'firmen'])) {
      const matches = companies;
      return {
        response: matches.length
          ? `I found ${matches.length} compan${matches.length === 1 ? 'y' : 'ies'} in your CRM.`
          : 'I do not have company records available in this view.',
        action: 'search_companies',
        data: matches,
        cards: matches.slice(0, 12).map(companyCard)
      };
    }

    const openTasks = tasks.filter(isOpenTask);
    const overdueTasks = openTasks.filter(task => new Date(task.dueDate as any) < today);
    const activeOpps = opportunities.filter(opp => opp.stage !== 'won' && opp.stage !== 'lost');

    return {
      response: `I can help with CRM data and app guidance. Current snapshot: ${contacts.length} contacts, ${openTasks.length} open tasks (${overdueTasks.length} overdue), and ${activeOpps.length} active opportunities. Try asking "show my overdue tasks", "where do I add a contact?", or "show hot leads".`,
      action: 'assistant_general',
      data: [],
      cards: []
    };
  }
};
